import React, { useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { toast } from "sonner";
import { API_URL } from "../../api/api";

const auth = () => {
  const t = localStorage.getItem("token");
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

const RegularisationModal = ({ onClose, onSuccess, employees = [] }) => {
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const userData = (() => {
    try { return JSON.parse(localStorage.getItem("userdata") || "{}"); }
    catch { return {}; }
  })();
  const myId = userData.id || localStorage.getItem("user_id");
  // Both admin and HR can submit on behalf of an employee — show the picker.
  const isManager = role === "hr" || role === "admin";

  const [form, setForm] = useState({
    user_id: myId || "",
    date: "",
    requested_in: "",
    requested_out: "",
    reason: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Set max date to today so future dates can't be regularised
  const today = new Date().toISOString().split("T")[0];

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.date || !form.requested_in || !form.requested_out || !form.reason.trim()) {
      setError("All fields are required.");
      return;
    }
    if (form.requested_out <= form.requested_in) {
      setError("Check-out time must be after check-in time.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/attendance/regularise`, form, auth());
      // Toast rather than an inline banner: the modal closes on success, so
      // there is no longer anywhere in the form for the confirmation to live.
      toast.success("Regularisation request submitted — awaiting approval.");
      onSuccess?.();
      onClose();
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.clear();
        window.location.assign("/login");
        return;
      }
      setError(err?.response?.data?.error || "Could not submit request.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 480 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="title-btns" style={{ marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0 }}>Regularise Attendance</h3>
            <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>
              Submit a request to correct a missed or wrong attendance record.
            </p>
          </div>
          <button
            className="clbtn"
            onClick={onClose}
            style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer" }}
          >
            ✕
          </button>
        </div>

        {error && (
          <div
            style={{
              marginBottom: 12,
              padding: "8px 12px",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 8,
              color: "#b91c1c",
              fontSize: 13,
            }}
          >
            ⚠ {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Admin + HR can submit on behalf of any employee */}
          {isManager && employees.length > 0 && (
            <div className="form-item">
              <label>Employee *</label>
              <select
                name="user_id"
                value={form.user_id}
                onChange={handleChange}
              >
                <option value="">Select Employee</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.first_name} {e.last_name}
                  </option>
                ))}
              </select>
              <small style={{ color: "#64748b" }}>
                Defaults to yourself if left unselected.
              </small>
            </div>
          )}

          <div className="form-item">
            <label>Date *</label>
            <input
              type="date"
              name="date"
              value={form.date}
              max={today}
              onChange={handleChange}
            />
          </div>

          <div className="date-row col-2">
            <div className="form-item">
              <label>Check-in Time *</label>
              <input
                type="time"
                name="requested_in"
                value={form.requested_in}
                onChange={handleChange}
              />
            </div>
            <div className="form-item">
              <label>Check-out Time *</label>
              <input
                type="time"
                name="requested_out"
                value={form.requested_out}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-item">
            <label>Reason *</label>
            <textarea
              name="reason"
              value={form.reason}
              onChange={handleChange}
              rows={3}
              placeholder="Briefly explain why attendance was not marked (e.g. forgot to clock in, device issue…)"
              style={{ resize: "vertical", minHeight: 72 }}
            />
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="org-gradi"
              disabled={saving}
            >
              {saving ? "Submitting…" : "Submit Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

RegularisationModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  employees: PropTypes.array,
};

export default RegularisationModal;
